import { styled } from "goober";
import { Box } from "../../Box";
import { Input } from "../../Input";
import { Text } from "../../Text";
import { emptyMapping, type SamlMapping } from "./constants";
import { Field, FieldActions, FieldLink, HelperRow, KeptValue } from "./shared";

const mappingFields: {
  key: keyof SamlMapping;
  label: string;
  required: boolean;
}[] = [
  { key: "firstName", label: "First name", required: true },
  { key: "lastName", label: "Last name", required: true },
  { key: "email", label: "Email", required: true },
  { key: "image", label: "Profile picture", required: false },
];

const Fields = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldGrid = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 12px;
  row-gap: 4px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export function AttributeNamesField({
  mapping,
  presets,
  replacing,
  onChange,
  existing,
}: {
  mapping: SamlMapping;
  presets: SamlMapping;
  replacing: boolean;
  onChange: (mapping: SamlMapping, replacing: boolean) => void;
  existing?: boolean;
}) {
  const keepingCurrent = existing && !replacing;

  return (
    <Fields>
      <Field>
        <Box horizontal={8} top={10}>
          <Text variant="text-small-md">Attribute names</Text>
        </Box>
        <Box horizontal={8} top={2} bottom={keepingCurrent ? 10 : 0}>
          <Text variant="text-small" color="var(--text-secondary)">
            The SAML attributes that hold each user’s email and name.
          </Text>
        </Box>
        {keepingCurrent && (
          <KeptValue
            label="Current attribute names are kept"
            onReplace={() => onChange({ ...presets }, true)}
          />
        )}
      </Field>
      {!keepingCurrent && (
        <FieldGrid>
          {mappingFields.map(({ key, label, required }) => (
            <Field key={key}>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">
                  {required || existing ? label : `${label} (optional)`}
                </Text>
              </Box>
              <Input
                aria-label={label}
                value={mapping[key]}
                onChange={(e) =>
                  onChange({ ...mapping, [key]: e.target.value }, replacing)
                }
                placeholder={
                  existing
                    ? "Leave blank to keep current"
                    : "SAML attribute name"
                }
                required={required && !existing}
                style={{ width: "100%", minWidth: 0 }}
              />
            </Field>
          ))}
        </FieldGrid>
      )}
      {existing && !keepingCurrent && (
        <HelperRow style={{ paddingTop: 2 }}>
          <FieldActions>
            <FieldLink onPress={() => onChange({ ...emptyMapping }, false)}>
              <Text variant="text-small">Keep current attribute names</Text>
            </FieldLink>
          </FieldActions>
        </HelperRow>
      )}
    </Fields>
  );
}
